import { useState } from "react"

import { useGetAssociatedTestTemplateList } from "../../TestTemplates/hooks"
import { ProductTestActionDialogType } from "../types"

import { useCancelQualityApproval } from "./useCancelQualityApproval"
import { useCreateQualityApproval } from "./useCreateQualityApproval"
import { useGetProductTestDocuments } from "./useGetProductTestDocuments"

import type { AssociatedTestTemplate, ProductTest, ProductTestResult, SignedUrl } from "@/graphql/codegen/graphql"
import { AssociatedTestTemplateSortField, SortingOrder } from "@/graphql/codegen/graphql"

interface UseProductTestActionsProps {
  productId: number
  onRefresh: () => void
}

interface UseProductTestActionsReturn {
  openedDialog: ProductTestActionDialogType | null
  associatedTestTemplates: AssociatedTestTemplate[]
  productTest: ProductTest | null
  productTestResult: ProductTestResult | null
  documents: SignedUrl[]
  isDocumentsFetching: boolean
  refetchDocuments: () => void
  handleFormDialogOpen: (productTest?: ProductTest) => void
  handleDeleteDialogOpen: (productTest: ProductTest) => void
  handleDocumentsDialogOpen: (productTest: ProductTest) => void
  handleOverrideDialogOpen: (productTest: ProductTest, productTestResult: ProductTestResult) => void
  handleApprovalRequest: (productTestResult: ProductTestResult) => Promise<void>
  handleApprovalCancelDialogOpen: (productTestResult: ProductTestResult) => void
  handleApprovalCancelConfirm: () => Promise<void>
  handleDialogClose: () => void
  handleDialogSubmit: () => void
}

export const useProductTestActions = ({
  productId,
  onRefresh,
}: UseProductTestActionsProps): UseProductTestActionsReturn => {
  const [openedDialog, setOpenedDialog] = useState<ProductTestActionDialogType | null>(null)
  const [productTest, setProductTest] = useState<ProductTest | null>(null)
  const [productTestResult, setProductTestResult] = useState<ProductTestResult | null>(null)
  const { associatedTestTemplates } = useGetAssociatedTestTemplateList({
    productId,
    field: AssociatedTestTemplateSortField.SequenceNumber,
    order: SortingOrder.Asc,
  })
  const {
    documents,
    isFetching: isDocumentsFetching,
    refetch: refetchDocuments,
  } = useGetProductTestDocuments(productTest?.productTestId)
  const { createQualityApproval } = useCreateQualityApproval()
  const { cancelQualityApproval } = useCancelQualityApproval()

  const handleFormDialogOpen = (productTest?: ProductTest) => {
    setOpenedDialog(ProductTestActionDialogType.Form)
    setProductTest(productTest ?? null)
  }

  const handleDeleteDialogOpen = (productTest: ProductTest) => {
    setOpenedDialog(ProductTestActionDialogType.DeleteConfirmation)
    setProductTest(productTest)
  }

  const handleDocumentsDialogOpen = (productTest: ProductTest) => {
    setOpenedDialog(ProductTestActionDialogType.Documents)
    setProductTest(productTest)
  }

  const handleOverrideDialogOpen = (productTest: ProductTest, productTestResult: ProductTestResult) => {
    setOpenedDialog(ProductTestActionDialogType.Override)
    setProductTest(productTest)
    setProductTestResult(productTestResult)
  }

  const handleApprovalRequest = async (productTestResult: ProductTestResult) => {
    const isSuccess = await createQualityApproval({ productTestResultId: productTestResult.productTestResultId })

    if (isSuccess) onRefresh()
  }

  const handleApprovalCancelDialogOpen = (productTestResult: ProductTestResult) => {
    setOpenedDialog(ProductTestActionDialogType.ApprovalCancelConfirmation)
    setProductTestResult(productTestResult)
  }

  const handleDialogClose = () => {
    setOpenedDialog(null)
    setProductTest(null)
    setProductTestResult(null)
  }

  const handleApprovalCancelConfirm = async () => {
    if (!productTestResult?.qualityApproval) return

    const isSuccess = await cancelQualityApproval({
      qualityApprovalId: productTestResult.qualityApproval.qualityApprovalId,
    })

    handleDialogClose()
    if (isSuccess) onRefresh()
  }

  const handleDialogSubmit = () => {
    handleDialogClose()
    onRefresh()
  }

  return {
    openedDialog,
    associatedTestTemplates,
    productTest,
    productTestResult,
    documents,
    isDocumentsFetching,
    refetchDocuments,
    handleFormDialogOpen,
    handleDeleteDialogOpen,
    handleDocumentsDialogOpen,
    handleOverrideDialogOpen,
    handleApprovalRequest,
    handleApprovalCancelDialogOpen,
    handleApprovalCancelConfirm,
    handleDialogClose,
    handleDialogSubmit,
  }
}
